/**
 * New lab limit version (F7.5-b). uc-core CreateLabLimitDto + LabLimitService.create:
 *   cropId? (empty = every crop) · panel ≤ 40 (stored upper case) · analyte? ≤ 80 (empty = panel default)
 *   rule NOT_DETECTED | MAX_LIMIT · limitValue /^\d+(\.\d{1,4})?$/ required for MAX_LIMIT
 *   maxLoq? same format · uom ≤ 10 · requiredForRelease? · source ≤ 120 · effectiveFrom (date)
 * A limit is never edited: saving adds the next version for (crop, panel, analyte).
 */

export type LabLimitRule = 'NOT_DETECTED' | 'MAX_LIMIT'

export const RULE_LABEL: Record<LabLimitRule, string> = {
  NOT_DETECTED: 'Must not be detected',
  MAX_LIMIT: 'At most a limit',
}

export interface LabLimitDraft {
  cropId: string
  panel: string
  analyte: string
  rule: LabLimitRule | ''
  limitValue: string
  maxLoq: string
  uom: string
  requiredForRelease: boolean
  source: string
  effectiveFrom: string
}

export type LabLimitProblems = Partial<Record<keyof LabLimitDraft, string>>

export const emptyLabLimitDraft = (today = ''): LabLimitDraft => ({
  cropId: '',
  panel: '',
  analyte: '',
  rule: '',
  limitValue: '',
  maxLoq: '',
  uom: '',
  requiredForRelease: false,
  source: '',
  effectiveFrom: today,
})

/** Start a new version from an existing row. */
export function draftFromLimit(l: {
  cropId: string | null
  panel: string
  analyte: string | null
  rule: string
  limitValue: string | null
  maxLoq: string | null
  uom: string
  requiredForRelease: boolean
  source: string
}, today: string): LabLimitDraft {
  return {
    cropId: l.cropId ?? '',
    panel: l.panel,
    analyte: l.analyte ?? '',
    rule: l.rule === 'MAX_LIMIT' || l.rule === 'NOT_DETECTED' ? l.rule : '',
    limitValue: trimDecimal(l.limitValue),
    maxLoq: trimDecimal(l.maxLoq),
    uom: l.uom,
    requiredForRelease: l.requiredForRelease,
    source: l.source,
    effectiveFrom: today,
  }
}

/** "0.0100" → "0.01"; "10.0000" → "10". */
export function trimDecimal(v: string | null | undefined): string {
  if (!v) return ''
  if (!v.includes('.')) return v
  return v.replace(/0+$/, '').replace(/\.$/, '')
}

const DEC = /^\d+(\.\d{1,4})?$/

export function validateLabLimit(d: LabLimitDraft): LabLimitProblems {
  const p: LabLimitProblems = {}
  const panel = d.panel.trim()
  if (!panel) p.panel = 'Name the test, e.g. PESTICIDE'
  else if (panel.length > 40) p.panel = 'At most 40 characters'
  if (d.analyte.trim().length > 80) p.analyte = 'At most 80 characters'
  if (!d.rule) p.rule = 'Choose the rule'
  const lv = d.limitValue.trim()
  if (d.rule === 'MAX_LIMIT' && !lv) p.limitValue = 'Enter the limit'
  else if (lv && !DEC.test(lv)) p.limitValue = 'A number with up to 4 decimals, e.g. 0.01'
  else if (lv && d.rule === 'MAX_LIMIT' && Number(lv) <= 0) p.limitValue = 'Must be more than 0'
  const loq = d.maxLoq.trim()
  if (loq && !DEC.test(loq)) p.maxLoq = 'A number with up to 4 decimals, e.g. 0.01'
  const uom = d.uom.trim()
  if (!uom) p.uom = 'Enter the unit, e.g. mg/kg'
  else if (uom.length > 10) p.uom = 'At most 10 characters'
  const source = d.source.trim()
  if (!source) p.source = 'Say where the limit comes from'
  else if (source.length > 120) p.source = 'At most 120 characters'
  if (!d.effectiveFrom) p.effectiveFrom = 'Pick the date it applies from'
  else if (!/^\d{4}-\d{2}-\d{2}$/.test(d.effectiveFrom)) p.effectiveFrom = 'Not a date'
  return p
}

export const labLimitOk = (p: LabLimitProblems) => Object.values(p).every((v) => !v)

/** POST /lab-limits body. Call only when valid. */
export function labLimitBody(d: LabLimitDraft) {
  const analyte = d.analyte.trim()
  const lv = d.limitValue.trim()
  const loq = d.maxLoq.trim()
  return {
    ...(d.cropId ? { cropId: d.cropId } : {}),
    panel: d.panel.trim().toUpperCase(),
    ...(analyte ? { analyte } : {}),
    rule: d.rule as LabLimitRule,
    ...(d.rule === 'MAX_LIMIT' && lv ? { limitValue: lv } : {}),
    ...(loq ? { maxLoq: loq } : {}),
    uom: d.uom.trim(),
    requiredForRelease: d.requiredForRelease,
    source: d.source.trim(),
    effectiveFrom: d.effectiveFrom,
  }
}

// ---------------- de-label run ----------------

/** POST /pack-lots/:id/delabel-runs (DelabelRunDto): operatorId, locationId, equipmentId?, inputKg (≤ 3 dp). */
export interface DelabelDraft {
  operatorId: string
  locationId: string
  equipmentId: string
  inputKg: string
}
export const emptyDelabelDraft = (): DelabelDraft => ({ operatorId: '', locationId: '', equipmentId: '', inputKg: '' })

export function validateDelabel(d: DelabelDraft): Partial<Record<keyof DelabelDraft, string>> {
  const p: Partial<Record<keyof DelabelDraft, string>> = {}
  if (!d.operatorId) p.operatorId = 'Pick who empties the pouches'
  if (!d.locationId) p.locationId = 'Pick the packing hall'
  const kg = d.inputKg.trim().replace(/,/g, '')
  if (!kg) p.inputKg = 'Enter the kilos of pouches emptied'
  else if (!/^\d+(\.\d{1,3})?$/.test(kg)) p.inputKg = 'Kilos with up to 3 decimals, e.g. 26.4'
  else if (Number(kg) <= 0) p.inputKg = 'Must be more than 0'
  return p
}

export function delabelBody(d: DelabelDraft) {
  return {
    operatorId: d.operatorId,
    locationId: d.locationId,
    ...(d.equipmentId ? { equipmentId: d.equipmentId } : {}),
    inputKg: d.inputKg.trim().replace(/,/g, ''),
  }
}
